import { Box, Button, Image, InlineNotification, Text } from "@familyzone/component-library";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { FC, useEffect, useState } from "react";
import StandardModal from "js/components/shared/StandardModal";
import iconClock from "assets/IconClock.svg?url";
import { convertEndTimeToSecsTillExpiry } from "js/utils/timeUtils";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { colours } from "utils/themeUtils";
import { ClassroomSessionTimeSelector } from "../ClassroomSessionTimeSelector";
import { oneMinuteFromNow, padTimes } from "../ClassroomSessionTimeSelectorHelpers";
import { useClassroomSessionStore } from "stores/v2/classroomSessionStore";
import { envConfig } from "@/config";
import { fetchLastSessionEndTime } from "js/api/sessionsApi";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@chakra-ui/react";
import { getClassroomStore } from "stores/classroomStore";
import useStore from "@/stores";

dayjs.extend(customParseFormat);

interface Props {
	open: boolean;
	onStartError: boolean;
	inClassroom: boolean;
	isMonitoringClass?: boolean;
	studentCount: number;
}

const ClassroomStartSession: FC<Props> = (props) => {
	const { classroomId, classroomStableId } = useSelectedClassroomStore((state) => ({
		classroomId: state.id,
		classroomStableId: state.stableIdV1,
	}));

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
	const teacherStableId = getClassroomStore(useStore.getState()).getTeacherStableIdV1() as string;

	const [timeValue, setTimeValue] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	// This error comes from timer and decides whether save button should be disabled
	const [hasTimeError, setHasTimeError] = useState<string | null>(null);
	const [studentCountError, setStudentCountError] = useState<string | null>(null);
	const [requestOngoing, setRequestOngoing] = useState<boolean | null>(null);

	const fetchLastSessionEndTimeQuery = useQuery({
		queryKey: [teacherStableId, classroomStableId],
		queryFn: async () => {
			// This is not possible. When a claas is selected, teacher and classroom stable IDs are already known. Just for type safety
			// If these aren't available, prefill time selector default to one minute from now
			if (!teacherStableId || !classroomStableId) {
				return;
			}
			return await fetchLastSessionEndTime(teacherStableId, classroomStableId);
		},
	});

	useEffect(() => {
		if (!props.open) {
			setTimeValue(null);
			setError(null);
		}
		if (props.onStartError) {
			setError("Cannot start the class due to time restrictions set by your school");
			setRequestOngoing(false);
		}
	}, [props.open, props.onStartError]);

	useEffect(() => {
		const MaxStudents = envConfig.MAX_STUDENTS;
		if (props.studentCount && props.studentCount > MaxStudents) {
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			setStudentCountError(`Cannot start the class with more than ${MaxStudents} students`);
		} else {
			setStudentCountError(null);
		}
	}, [props.studentCount]);

	const timeSelectedHandler = (value: number | null, hasTimeError: string | null) => {
		setTimeValue(value);
		setError(null);
		setHasTimeError(hasTimeError);
		setRequestOngoing(false);
	};

	const handleStartMonitoringSession = () => {
		if (timeValue) {
			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: convertEndTimeToSecsTillExpiry(timeValue),
				monitoringClass: true,
				force: false,
				inClassroom: props.inClassroom,
			});

			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);
		} else if (!props.onStartError) {
			setError("Please select a valid time");
		}
	};

	const handleStartRegularSession = () => {
		if (timeValue) {
			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: convertEndTimeToSecsTillExpiry(timeValue),
				monitoringClass: false,
				inClassroom: props.inClassroom,
			});

			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);
		} else if (!props.onStartError) {
			setError("Please select a valid time");
		}
	};

	const handleStartSession = () => {
		if (!timeValue) return;
		// Ensure the end time is in the future
		const timeObject = dayjs(padTimes(timeValue), "HHmm", true);
		if (timeObject.isBefore(oneMinuteFromNow())) {
			setError("End time cannot be in the past");
			return;
		}

		if (props.isMonitoringClass) {
			handleStartMonitoringSession();
		} else {
			handleStartRegularSession();
		}
	};

	const handleClose = () => {
		useClassroomSessionStore.getState().handleShowStartClassModal({ show: false });
		setTimeValue(null);
		setError(null);
		setRequestOngoing(false);
	};

	const renderErrorText = () => {
		if ((error === null || error === "") && (studentCountError === null || studentCountError === "")) {
			return <></>;
		}

		const errorText = studentCountError ?? error;
		return (
			<Text position="absolute" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
				{errorText}
			</Text>
		);
	};

	const renderActions = () => (
		<Button
			variant="primary"
			onClick={handleStartSession}
			isLoading={requestOngoing ?? false}
			isDisabled={error !== null || hasTimeError !== null || studentCountError !== null}
		>
			Start
		</Button>
	);

	const queryAndRenderSessionEndTime = () => {
		if (fetchLastSessionEndTimeQuery.isLoading) {
			return <Spinner size="sm" />;
		}

		let preselected = oneMinuteFromNow().format("hh:mm A");
		if (fetchLastSessionEndTimeQuery.data && fetchLastSessionEndTimeQuery.data.lastEndTime > 0) {
			preselected = dayjs.unix(fetchLastSessionEndTimeQuery.data.lastEndTime).format("hh:mm A");
		}

		return (
			<Box>
				<ClassroomSessionTimeSelector onChange={timeSelectedHandler} preselected={preselected} />
				{renderErrorText()}
			</Box>
		);
	};

	const render = () => {
		return (
			<StandardModal
				size="medium"
				title={props.isMonitoringClass ? "Start Monitoring Session" : "Start Class Session"}
				actions={renderActions()}
				open={props.open}
				close={handleClose}
			>
				<form onSubmit={(ev) => ev.preventDefault()}>
					<Box className="ClassroomSession_label">
						<InlineNotification
							status="info"
							notificationDescription={
								<>
									Please enter time in <b>hh:mm</b> format
								</>
							}
						/>
					</Box>
					<Box className="ClassroomSession_label">
						<Image className="ClassroomSession_labelIcon" src={iconClock} alt=""></Image>
						<Text className="ClassroomSession_labelText">Session ends at</Text>
						{queryAndRenderSessionEndTime()}
					</Box>
				</form>
			</StandardModal>
		);
	};

	return render();
};

export default ClassroomStartSession;
